import videojs from 'video.js';
import SetInterval from 'set-interval';
import Timeout from 'smart-timeout';

import { videoJsOptions, clipperVideoJsOptions } from '../constants/videoJsOptions';

const { id } = videoJsOptions;
const clipperId = clipperVideoJsOptions.id;
const msPerSecond = 1000;

// It seems there is no simple/reliable way to get the frame rate of a video with JavaScript, so it is hardcoded here.
const FRAME_RATE = 30;
export const oneFrameInSec = 1 / FRAME_RATE;
export const oneFrameInMs = oneFrameInSec * msPerSecond;

export function getPlayerInstance() {
  // Hack to clear interval
  SetInterval.clear('rewind');
  return videojs(id);
}

export function getVirtualClipperPlayerInstance() {
  SetInterval.clear('rewind');
  return videojs(clipperId);
}

export function getCurrentTime() {
  return videojs(id).currentTime();
}

export function getClipperCurrentTime() {
  return videojs(clipperId).currentTime();
}

export function setClipperCurrentTime(time) {
  const videoPlayer = getVirtualClipperPlayerInstance();
  videoPlayer.currentTime(time);
}

export function pauseAndSetClipperCurrentTime(time) {
  const videoPlayer = getVirtualClipperPlayerInstance();
  videoPlayer.pause();
  videoPlayer.currentTime(time);
}

export function getDuration() {
  return videojs(id).duration();
}

export function togglePause(e) {
  e.preventDefault();
  const videoPlayer = getVirtualClipperPlayerInstance();
  if (videoPlayer.paused()) {
    safePlay(videoPlayer);
  } else {
    videoPlayer.pause();
  }
}

function navigateSeconds(seconds, direction) {
  const videoPlayer = getVirtualClipperPlayerInstance();
  const currentPlayTime = getClipperCurrentTime();

  videoPlayer.currentTime(currentPlayTime + (direction === 'forward' ? seconds : -seconds));
}

export function oneSecondForward() {
  navigateSeconds(1, 'forward');
}

export function oneSecondBack() {
  navigateSeconds(1, 'back');
}

export function fiveSecondsForward() {
  navigateSeconds(5, 'forward');
}

export function fiveSecondsBack() {
  navigateSeconds(5, 'back');
}

function navigateOneFrame(direction) {
  const videoPlayer = getVirtualClipperPlayerInstance();
  const currentPlayTime = getClipperCurrentTime();
  if (!videoPlayer.paused()) {
    videoPlayer.pause();
  }

  videoPlayer.currentTime(currentPlayTime + (direction === 'forward' ? 1 : -1) / FRAME_RATE);
}

export function oneFrameForward() {
  navigateOneFrame('forward');
}

export function oneFrameBack() {
  navigateOneFrame('back');
}

export function startVideoPreviewInterval(markInTime, markOutTime) {
  const videoPlayer = getVirtualClipperPlayerInstance();
  const playbackRate = videoPlayer.playbackRate();
  const durationInMs = ((markOutTime - markInTime) * msPerSecond) / playbackRate;

  SetInterval.clear('repeatVideoPreview');
  SetInterval.start(
    () => {
      videoPlayer.currentTime(markInTime);
    },
    durationInMs,
    'repeatVideoPreview',
  );
}

export function previewVideo(markInTime, markOutTime) {
  const videoPlayer = getVirtualClipperPlayerInstance();

  videoPlayer.currentTime(markInTime);
  startVideoPreviewInterval(markInTime, markOutTime);
  safePlay(videoPlayer);
}

export function stopVideoPreview() {
  const videoPlayer = getVirtualClipperPlayerInstance();

  SetInterval.clear('repeatVideoPreview');

  videoPlayer.pause();
}

export function updateMarkOutOfReviewedVideo(markInTime, markOutTime) {
  const videoPlayer = getVirtualClipperPlayerInstance();

  videoPlayer.currentTime(markInTime);
  startVideoPreviewInterval(markInTime, markOutTime);
}

const waitFor = (ms) => new Promise((res) => Timeout.set('playClipTimeout', res, ms));

// Helper to safely call play() and catch autoplay policy errors
const safePlay = (player) => {
  const playPromise = player.play();
  if (playPromise !== undefined) {
    playPromise.catch(() => {
      // Autoplay was prevented - this is expected behavior
    });
  }
};

export async function playClip(markInTime, markOutTime) {
  const videoPlayer = getVirtualClipperPlayerInstance();
  const playbackRate = videoPlayer.playbackRate();
  const durationInMs = ((markOutTime - markInTime) * msPerSecond) / playbackRate;

  videoPlayer.currentTime(markInTime);
  await waitFor(50);
  safePlay(videoPlayer);
  await waitFor(durationInMs);
  videoPlayer.pause();
}

export function stopClip() {
  if (Timeout.exists('playClipTimeout')) {
    Timeout.clear('playClipTimeout');
  }

  const videoPlayer = getVirtualClipperPlayerInstance();
  videoPlayer.pause();
}
